import {useId} from "react";
import BaseButton from "../components/BaseButton";
import BaseInput from "../components/BaseInput";
import {useLocale, useMessages} from "i18n";
import {
	CANCELLATION_LIMITS,
	type ContractCancellationDraft,
	type ContractCancellationKind,
	formatCancellationReceipt,
	formatRequestedEffectiveDate,
} from "./contractCancellation";
import {useContractCancellation} from "./useContractCancellation";
import * as S from "./CancelContractPage.styles";

/**
 * The § 312k BGB confirmation page (kitecorp/transferify-ui#2327): the page the statutory cancellation
 * control leads to, and the page the declaration is given on.
 *
 * <p><strong>Reachable with no session, and that is the requirement.</strong> OLG Nürnberg 3 U 2214/23, OLG
 * Köln 6 U 62/24, LG München I 33 O 15098/22 (confirmed OLG München 6 U 4336/23e) and KG Berlin 5 UKl 10/25
 * each held that asking a consumer to log in, or for a customer number plus a password, before the control or
 * this page breaks the norm. So there is no `PrivateRoute`, no login prompt, and the route is pre-rendered
 * rather than client-only.
 *
 * <p><strong>Abs. 2 S. 3 is exhaustive, so this page is a heading, five labelled inputs and the button —
 * and nothing else</strong> (ui#2601). BGH I ZR 200/25 (FitX, 16.07.2026) reads the sentence as naming
 * everything the confirmation page may hold, and LG Köln 33 O 355/22 reads the field list as a maximum as
 * well as a minimum. That bars the obvious additions — a retention offer, a pause offer, a survey, a plan
 * comparison, a password prompt — and equally the harmless-looking ones this page used to carry: an intro
 * paragraph, a hint under each field and two "what happens next" sentences. Explanation belongs AFTER the
 * declaration, where the statute says nothing: the confirmation view below and the Abs. 4 acknowledgement
 * email already carry every sentence that was removed (see `i18n/de/cancellation.ts`, which maps them one
 * by one). `e2e/contract-cancellation-host.spec.ts` asserts this page's whole visible text against a
 * literal, so anything added here turns that spec red rather than reaching a German consumer.
 *
 * <p>Dumb by design: every rule it renders lives in `contractCancellation.ts`, and every piece of state comes
 * from `useContractCancellation`. The one thing this file decides is markup.
 */
export function CancelContractPage() {
	const {cancellation} = useMessages();
	const {locale} = useLocale();
	const {draft, setField, errors, submitting, failure, receivedAt, declared, submit} =
		useContractCancellation();
	const effectiveDateId = useId();

	const receipt = formatCancellationReceipt(receivedAt, locale);
	if (declared !== null && receipt !== null) {
		return (
			<S.Root>
				<S.Heading>{cancellation.done.title}</S.Heading>
				<S.DoneCard>
					{/* Names the control, the date and the time in one sentence: § 312k Abs. 3 asks the
					    consumer to be able to keep a record showing the declaration was given by pressing
					    that button, which a bare timestamp would not show. */}
					<S.Received>{cancellation.done.received(receipt)}</S.Received>
					<S.SummaryTitle>{cancellation.done.summaryTitle}</S.SummaryTitle>
					<DeclarationSummary declared={declared}/>
					<S.NextNote>{cancellation.done.emailSent(declared.email.trim())}</S.NextNote>
					<S.NextNote>{cancellation.done.keep}</S.NextNote>
					{/* The offer that makes Abs. 3 true regardless of the email's fate: a mistyped address
					    means no confirmation arrives and the consumer would otherwise hold nothing. */}
					<S.PrintAction type="button" onClick={() => window.print()}>
						{cancellation.done.save}
					</S.PrintAction>
				</S.DoneCard>
			</S.Root>
		);
	}

	return (
		<S.Root>
			<S.Heading>{cancellation.heading}</S.Heading>
			<S.Form
				noValidate
				onSubmit={(event) => {
					event.preventDefault();
					void submit();
				}}
			>
				{/* Abs. 2 S. 3 Nr. 1 lit. e — the address the confirmation is to reach the consumer at — and
				    half of lit. b, since it is also what identifies them. The only required field besides the
				    reason, and `required` is what renders the marker the label carries. */}
				<S.Field>
					<BaseInput
						label={cancellation.fields.email.label}
						type="email"
						required
						autoComplete="email"
						value={draft.email}
						error={errors.email !== undefined}
						inputProps={{maxLength: CANCELLATION_LIMITS.email}}
						onChange={(event) => setField("email", event.target.value)}
					/>
					{errors.email !== undefined && (
						<S.FieldError>{cancellation.errors[errors.email]}</S.FieldError>
					)}
				</S.Field>

				{/* lit. b, "eindeutige Identifizierbarkeit". Optional, and the label says so. */}
				<S.Field>
					<BaseInput
						label={cancellation.fields.name.label}
						autoComplete="name"
						value={draft.name}
						error={errors.name !== undefined}
						inputProps={{maxLength: CANCELLATION_LIMITS.name}}
						onChange={(event) => setField("name", event.target.value)}
					/>
					{errors.name !== undefined && (
						<S.FieldError>{cancellation.errors[errors.name]}</S.FieldError>
					)}
				</S.Field>

				{/* lit. c, "eindeutige Bezeichnung des Vertrags". */}
				<S.Field>
					<BaseInput
						label={cancellation.fields.contract.label}
						value={draft.contractDetail}
						error={errors.contractDetail !== undefined}
						inputProps={{maxLength: CANCELLATION_LIMITS.contractDetail}}
						onChange={(event) => setField("contractDetail", event.target.value)}
					/>
					{errors.contractDetail !== undefined && (
						<S.FieldError>{cancellation.errors[errors.contractDetail]}</S.FieldError>
					)}
				</S.Field>

				{/* lit. a. A real fieldset/legend pair, and NOTHING checked to begin with: Abs. 2 S. 3 Nr. 1
				    lit. a asks the page to invite the consumer to state the kind, and a preselected radio
				    states it for them. */}
				<S.KindGroup>
					<S.KindLegend>{cancellation.fields.kind.label}</S.KindLegend>
					<KindChoice
						kind="ORDINARY"
						label={cancellation.fields.kind.ordinary}
						checked={draft.kind === "ORDINARY"}
						onChoose={() => setField("kind", "ORDINARY")}
					/>
					<KindChoice
						kind="EXTRAORDINARY"
						label={cancellation.fields.kind.extraordinary}
						checked={draft.kind === "EXTRAORDINARY"}
						onChoose={() => setField("kind", "EXTRAORDINARY")}
					/>
					{errors.kind !== undefined && (
						<S.FieldError>{cancellation.errors[errors.kind]}</S.FieldError>
					)}
				</S.KindGroup>

				{/* Appears only for the extraordinary choice, because only that one has a reason to state
				    (Abs. 2 S. 3 Nr. 1 lit. a). Rendering it always would put a field on the page that the
				    statute does not ask for in the ordinary case. */}
				{draft.kind === "EXTRAORDINARY" && (
					<S.Field>
						<BaseInput
							label={cancellation.fields.reason.label}
							required
							multiline
							minRows={3}
							value={draft.reason}
							error={errors.reason !== undefined}
							inputProps={{maxLength: CANCELLATION_LIMITS.reason}}
							onChange={(event) => setField("reason", event.target.value)}
						/>
						{errors.reason !== undefined && (
							<S.FieldError>{cancellation.errors[errors.reason]}</S.FieldError>
						)}
					</S.Field>
				)}

				{/* lit. d, the moment the consumer wants the contract to end. A plain labelled date control
				    rather than a `BaseInput` — see the styles file: MUI's floating label overlaps a native
				    date input's own placeholder until the field is focused. */}
				<S.Field>
					<S.NativeLabel htmlFor={effectiveDateId}>
						{cancellation.fields.effectiveDate.label}
					</S.NativeLabel>
					<S.DateInput
						id={effectiveDateId}
						type="date"
						value={draft.requestedEffectiveDate}
						aria-invalid={errors.requestedEffectiveDate !== undefined}
						onChange={(event) => setField("requestedEffectiveDate", event.target.value)}
					/>
					{errors.requestedEffectiveDate !== undefined && (
						<S.FieldError>{cancellation.errors[errors.requestedEffectiveDate]}</S.FieldError>
					)}
				</S.Field>

				<S.SubmitRow>
					{/* § 312k Abs. 2 S. 3 Nr. 2: labelled with nothing but the act. Never disabled on an invalid
					    draft — pressing it is how the reader learns what is missing, and a control that
					    looks dead is the "not easily accessible" complaint LG München I upheld. */}
					<BaseButton type="submit" disabled={submitting}>
						{submitting ? cancellation.submitting : cancellation.submit}
					</BaseButton>
					{failure !== null && <S.FormError>{cancellation.errors[failure]}</S.FormError>}
				</S.SubmitRow>
			</S.Form>
		</S.Root>
	);
}

/** One kind of termination: a radio and its label, which is all Abs. 2 S. 3 Nr. 1 lit. a invites. */
function KindChoice({kind, label, checked, onChoose}: {
	kind: ContractCancellationKind;
	label: string;
	checked: boolean;
	onChoose: () => void;
}) {
	const radioId = useId();
	return (
		// A DIV with an explicit <label>, rather than one <label> wrapping the control. Each radio's
		// accessible name is then exactly the option's name — the string a screen-reader user hears and the
		// one every spec addresses the control by — with nothing else in the row able to join it.
		<S.KindOption>
			<S.KindRadio
				id={radioId}
				type="radio"
				name="cancellation-kind"
				value={kind}
				checked={checked}
				onChange={onChoose}
			/>
			<S.KindOptionLabel htmlFor={radioId}>{label}</S.KindOptionLabel>
		</S.KindOption>
	);
}

/**
 * What was declared, restated from the SNAPSHOT the hook kept rather than from the live form.
 *
 * Optional rows the reader left empty are omitted, and the requested-date row falls back to the statutory
 * default (Abs. 5) rather than showing a blank: an empty labelled row reads as information lost, and this
 * view is the consumer's proof.
 */
function DeclarationSummary({declared}: {declared: ContractCancellationDraft}) {
	const {cancellation} = useMessages();
	const {locale} = useLocale();
	const kindLabel = declared.kind === "EXTRAORDINARY"
		? cancellation.fields.kind.extraordinary
		: cancellation.fields.kind.ordinary;
	const rows: readonly {label: string; value: string}[] = [
		{label: cancellation.done.email, value: declared.email.trim()},
		{label: cancellation.done.kind, value: kindLabel},
		...(declared.kind === "EXTRAORDINARY" && declared.reason.trim().length > 0
			? [{label: cancellation.done.reason, value: declared.reason.trim()}]
			: []),
		...(declared.contractDetail.trim().length > 0
			? [{label: cancellation.done.contract, value: declared.contractDetail.trim()}]
			: []),
		{
			label: cancellation.done.effectiveRequested,
			// Formatted through the domain rule, never the raw YYYY-MM-DD the date input holds: this page is
			// the reader's proof and must read like the confirmation email, which writes the date out in full.
			value: formatRequestedEffectiveDate(declared.requestedEffectiveDate, locale)
				?? cancellation.done.effectiveEarliest,
		},
	];
	return (
		<S.Summary>
			{rows.map((row) => (
				<S.SummaryRow key={row.label}>
					<dt>{row.label}</dt>
					<dd>{row.value}</dd>
				</S.SummaryRow>
			))}
		</S.Summary>
	);
}
